// Host-page assembly for the WEB client — the parity core.
//
// This MUST stay byte-compatible with the render service's assembleHost (doc-render-service-design
// §4 / §7d): same runtime.js, same curated libs, same structure, so the on-screen preview equals the
// backend PDF. The libs are vendored as strings (./libs/*.lib.js, auto-generated from the service)
// and inlined so the sandboxed iframe needs no network.
import RUNTIME_JS from './libs/runtime.lib.js';
import CHART_JS from './libs/chart.lib.js';
import HANDLEBARS_JS from './libs/handlebars.lib.js';
import QRCODE_JS from './libs/qrcode.lib.js';

// Same files, same ORDER as the service's loader (runtime/libs/*.js, sorted by name, joined with
// "\n;\n"): chart.min.js, handlebars.min.js, qrcode.js. Chart.js 3.8.0 (pinned) exposes
// window.Chart for inistate.chart() -- analytic proposal §8b.
const CURATED_LIBS = [CHART_JS, HANDLEBARS_JS, QRCODE_JS].join('\n;\n');

const PRINT_RESET = [
  '*{box-sizing:border-box}',
  'html,body{margin:0;padding:0}',
  'body{-webkit-print-color-adjust:exact;print-color-adjust:exact}',
  '#inistate-doc-root{width:100%}',
].join('\n');

// <, >, & and U+2028/U+2029 — built from ASCII escapes only.
const UNSAFE_JSON = new RegExp('[<>&]|\\u2028|\\u2029', 'g');
const UNSAFE_MAP = { 0x3c: '\\u003C', 0x3e: '\\u003E', 0x26: '\\u0026', 0x2028: '\\u2028', 0x2029: '\\u2029' };

export function safeJson(data) {
  return JSON.stringify(data == null ? {} : data).replace(UNSAFE_JSON, (ch) => UNSAFE_MAP[ch.charCodeAt(0)] || ch);
}

function pageCss(page) {
  page = page || {};
  const size = page.width && page.height ? page.width + ' ' + page.height : page.size || 'A4';
  const orientation = page.orientation === 'landscape' ? ' landscape' : '';
  const margin = page.margin || '20mm';
  return '@page{size:' + size + orientation + ';margin:' + margin + '}';
}

/** Assemble the full host document for an iframe srcdoc. */
export function assembleHost(args) {
  const bundle = args.bundle || {};
  const page = args.page || {};
  const mode = bundle.mode || 'handlebars';

  const head = [
    '<!doctype html><html><head>',
    '<meta charset="utf-8">',
    '<base href="about:blank">',
    '<style>' + PRINT_RESET + '\n' + pageCss(page) + '</style>',
    '<style>' + (bundle.css || '') + '</style>',
    '<script>' + CURATED_LIBS + '</' + 'script>',
    '<script>' + RUNTIME_JS + '</' + 'script>',
    '<script>window.__INISTATE_DOC__=' + safeJson(args.data) + '</' + 'script>',
    '</head><body>',
  ].join('\n');

  const body =
    mode === 'js'
      ? '<main id="inistate-doc-root">' + (bundle.html || '') + '</main>'
      : '<main id="inistate-doc-root"></main>\n<script id="inistate-template" type="text/x-handlebars-template">' +
        (bundle.html || '') +
        '</' +
        'script>';

  const bootScript = [
    '<script>',
    'try{',
    '  inistate.__boot(window.__INISTATE_DOC__);',
    '  ' + (bundle.js || ''),
    '  inistate.__run();',
    '}catch(e){ inistate.__fail(e); }',
    '</' + 'script>',
  ].join('\n');

  return head + '\n' + body + '\n' + bootScript + '\n</body></html>';
}
